/* eslint-disable no-param-reassign */
import {
  createAsyncThunk, createSelector, createSlice, PayloadAction, SerializedError,
} from '@reduxjs/toolkit';
import {
  getDraftsForThreadApi,
  getDraftsForThreadV2Api,
  getMarkSolutionV2Api,
  getPinInfoApi,
  getPinInfoV2Api,
  getPinThreadApi,
  getPinThreadV2Api,
  getPostApi,
  getPostV2Api,
  getRepliesV2Api,
  getSubmitPostV2Api,
  getThreadV2Api,
  getVoteV2Api,
  submitPostApi,
  THREAD_API,
} from 'src/constants/apiEndpoints';
import { DiscussionPostCreationErrors } from 'src/constants/errorStrings';
import {
  ContentMetadata,
  CursorDirection,
  DeletePostRequest,
  DraftPostType,
  FilterOptions,
  GetDraftRequest,
  GetDraftResponse,
  GetPinsForThreadResponse,
  MarkSolutionRequest,
  OpPost,
  PinInfoRequest,
  PinInfoResponse,
  PostErrors,
  PostRepliesRequest,
  PostRepliesResponse,
  PostSuccess,
  PostType,
  ReplyPost,
  SortOptions,
  ThreadDetailPostState,
  ThreadDetailRequest,
  ThreadDetailResponse,
  ThreadDetailSubmitPostRequest,
  ThreadDetailSubmitPostResponse,
  ThreadDetailSubmitVoteRequest,
  ThreadDetailSubmitVoteResponse,
  UpdatePinsForThreadRequest,
  UpdatePinsForThreadResponse,
} from 'src/types/DiscussionPost';
import { ErrorType } from 'src/types/Errors';
import FetchStatus from 'src/types/FetchStatus';
import {
  constructQueryParams, customFetch, getFieldErrorList, parseErrorResponse,
} from 'src/utils';
import { selectIsWeblabIdInT1 } from 'src/redux/slices/weblab';
import WeblabId from 'src/weblab/weblabId';
import { selectMarketplaceId } from './userInfo';
import type { AppState } from '../store';

interface PostsState {
  [key: string]: ThreadDetailPostState
}

export const CancelFetchThread = 'CancelThreadRequest';

export const commonPostActionErrors = {
  [ErrorType.actionForbidden]: PostErrors.actionForbidden,
  [ErrorType.unauthorizedAction]: PostErrors.unauthorizedAction,
  [ErrorType.opNotSameMarketplace]: PostErrors.opNotSameMarketplace,
  [ErrorType.opContainsReplies]: PostErrors.opContainsReplies,
  [ErrorType.unauthorizedMarketplaceId]: PostErrors.unauthorizedMarketplaceId,
  [ErrorType.silenced]: PostErrors.silenced,
  [ErrorType.invalidContent]: PostErrors.invalidContent,
};

export interface ThreadDetailState {
  status: FetchStatus,
  error?: string,
  hasNoAccessToPrivateDiscussion?: boolean;
  parentPostPreviousError?: string,
  parentPostNextError?: string,
  parentPostIds: Array<string>,
  posts: PostsState,
  previousCursor?: string,
  nextCursor?: string,
  isPreviousLoading: boolean,
  isNextLoading: boolean,
  filterBy: FilterOptions,
  sortBy: SortOptions,
  focusedPostId?: string
  draftPost?: {
    parentPostId: string,
    id?: string,
    type: DraftPostType,
  },
  draftReplyId?: string,
  draftSubmissionStatus: FetchStatus,
  draftFieldErrors?: Array<{ errorField: string, errors: string }>,
  draftPostErrors?: Array<PostErrors>
  draftContent?: string
  draftContentMetadata?: ContentMetadata,
  getDraftStatus: FetchStatus
  deleteDraftStatus: FetchStatus
  deletePostStatus: FetchStatus
  markSolutionStatus: FetchStatus
  replyCount: number
  solutionPost?: ReplyPost
  pinInfoResponse?: PinInfoResponse
  pinInfoFetchStatus?: FetchStatus
  pinUpdateStatus?: FetchStatus
  // Flag to denote a server initialized thread
  isServerInitializedThread?: boolean
}

export const initialState: ThreadDetailState = {
  status: FetchStatus.idle,
  posts: {},
  parentPostIds: [],
  isNextLoading: false,
  isPreviousLoading: false,
  filterBy: FilterOptions.allReplies,
  sortBy: SortOptions.oldest,
  draftPost: undefined,
  draftSubmissionStatus: FetchStatus.idle,
  getDraftStatus: FetchStatus.idle,
  deletePostStatus: FetchStatus.idle,
  deleteDraftStatus: FetchStatus.idle,
  markSolutionStatus: FetchStatus.idle,
  draftReplyId: undefined,
  replyCount: 0,
  error: undefined,
  hasNoAccessToPrivateDiscussion: undefined,
  parentPostPreviousError: undefined,
  parentPostNextError: undefined,
  previousCursor: undefined,
  nextCursor: undefined,
  focusedPostId: undefined,
  draftFieldErrors: undefined,
  draftPostErrors: undefined,
  draftContent: undefined,
  draftContentMetadata: undefined,
  solutionPost: undefined,
  pinInfoResponse: undefined,
  pinInfoFetchStatus: undefined,
  pinUpdateStatus: undefined,
  isServerInitializedThread: undefined,
};

/**
 * Utility functions
 */

const addNewPosts = (
  posts: PostsState,
  newPosts: (ReplyPost | OpPost)[],
) => newPosts.reduce((aggregatePosts, post) => ({
  ...aggregatePosts,
  [post.id]: {
    ...posts[post.id],
    errors: [],
    success: [],
    childrenPostIds: [],
    postDetails: post,
  },
}), posts);

const addPostIds = (
  postIds: Array<string>,
  newPosts: (ReplyPost | OpPost)[],
  direction: CursorDirection = CursorDirection.next,
) => {
  const newPostIds = newPosts.filter((post) => post.postType !== PostType.op)
    .map((post) => post.id);
  if (direction === CursorDirection.previous) {
    postIds.unshift(...newPostIds);
  } else {
    postIds.push(...newPostIds);
  }
  return postIds;
};

const getOpPost = (posts: PostsState) => Object.values(posts)
  .find(
    (post) => post.postDetails.postType === PostType.op,
  )?.postDetails as OpPost;

const addPostResponseStatus = <T>(postStatus: Array<T>, newStatus: Array<T>) => {
  const updatedStatus = [...postStatus, ...newStatus];
  return [...new Set(updatedStatus)];
};

export const getErrors = (
  { status, data }: { status: number, data: any },
  allowedPostErrorMap: any,
) => {
  const fieldErrors = [];
  const actionErrors: PostErrors[] = [];
  if ([400, 403].includes(status)) {
    const parsedErrorResponse = parseErrorResponse(data);
    if (parsedErrorResponse?.fieldErrors) {
      fieldErrors.push(
        ...getFieldErrorList(
          parsedErrorResponse.fieldErrors,
          DiscussionPostCreationErrors,
        ),
      );
    }
    if (parsedErrorResponse?.actionErrors) {
      const draftPostErrors = parsedErrorResponse.actionErrors
        .filter((actionError) => Object.keys(allowedPostErrorMap).includes(actionError))
        .map((actionError) => allowedPostErrorMap[actionError]);
      actionErrors.push(...draftPostErrors);
    }
  }
  return { fieldErrors, actionErrors };
};

const setThreadState = (
  state: ThreadDetailState,
  payload: ThreadDetailResponse,
  args: Omit<ThreadDetailRequest, 'threadId'>,
) => {
  state.status = FetchStatus.complete;
  const {
    direction, cursor, sortBy, filterBy,
  } = args;
  const {
    posts, repliesMap, repliesCount, solution,
  } = payload;
  const newParentPosts = posts.entries;

  state.sortBy = sortBy || state.sortBy;
  state.filterBy = filterBy || state.filterBy;
  // parent post loading states
  if (direction === CursorDirection.previous) {
    state.isPreviousLoading = false;
  } else {
    state.isNextLoading = false;
  }

  if (solution) {
    state.posts = addNewPosts(state.posts, [solution]);
  }
  // update post state with new parent & replies posts
  if (newParentPosts && newParentPosts.length > 0) {
    state.posts = addNewPosts(state.posts, newParentPosts);
    state.parentPostIds = addPostIds(
      cursor ? state.parentPostIds : [],
      newParentPosts,
      direction,
    );
  }

  if (repliesMap && Object.keys(repliesMap).length > 0) {
    // we replace the existing childrenpostIds since we dont have context on its position
    Object.keys(repliesMap).forEach((parentPostId) => {
      const {
        nextCursor,
        previousCursor,
        entries: newReplies,
      } = repliesMap[parentPostId];
      state.posts = addNewPosts(state.posts, newReplies);
      state.posts[parentPostId].childrenPostIds = addPostIds([], newReplies);
      state.posts[parentPostId].previousCursor = previousCursor;
      state.posts[parentPostId].nextCursor = nextCursor;
      state.posts[parentPostId].showReplies = true;
    });
  }

  // update cursors based on pagination request direction
  if (direction === CursorDirection.previous) {
    state.previousCursor = newParentPosts.length > 0
      ? payload.posts.previousCursor : undefined;
  } else if (direction === CursorDirection.next) {
    state.nextCursor = newParentPosts.length > 0
      ? payload.posts.nextCursor : undefined;
  } else {
    state.nextCursor = payload.posts.nextCursor;
    state.previousCursor = payload.posts.previousCursor;
  }

  state.replyCount = repliesCount === undefined ? state.replyCount : repliesCount;
  state.solutionPost = solution || state.solutionPost;
};

const getIsThreadDetailSSR = (state: AppState) => selectIsWeblabIdInT1(
  state,
  WeblabId.SELLER_FORUMS_THREAD_DETAILS_SSR,
);
/**
 * Async actions
 */

export const fetchReplies = createAsyncThunk(
  'threadDetail/fetchReplies',
  async (
    {
      threadId,
      postId,
      cursor,
      direction: cursorDirection,
      filterBy,
    }: PostRepliesRequest,
    {
      rejectWithValue,
      getState,
    },
  ) => {
    const apiPath = getIsThreadDetailSSR(getState() as AppState)
      ? getRepliesV2Api(threadId, postId)
      : `${THREAD_API}/${threadId}/post/${postId}/replies`;
    const apiEndpoint = constructQueryParams({
      cursorDirection, cursor, filterBy,
    }, apiPath);
    const repliesResponse = await customFetch(
      apiEndpoint,
      undefined,
      (error: Error) => rejectWithValue(error.message),
      1,
    ) as PostRepliesResponse;
    return repliesResponse;
  },
);

export const fetchThread = createAsyncThunk(
  'threadDetail/fetchThread',
  async (
    {
      threadId,
      cursor,
      direction: cursorDirection,
      postId,
      sortBy = SortOptions.oldest,
      filterBy = FilterOptions.allReplies,
    }: ThreadDetailRequest,
    { rejectWithValue, signal, getState },
  ) => {
    const apiPath = getIsThreadDetailSSR(getState() as AppState)
      ? getThreadV2Api(threadId)
      : `${THREAD_API}/${threadId}`;
    const apiEndpoint = constructQueryParams({
      sortBy, filterBy, postId, cursorDirection, cursor,
    }, apiPath);
    return await customFetch(
      apiEndpoint,
      { signal },
      (error: Error, responseInfo) => rejectWithValue({ message: error.message, responseInfo }),
      1,
    ) as ThreadDetailResponse;
  },
);

export const submitVote = createAsyncThunk(
  'threadDetail/submitVote',
  async (
    {
      threadId,
      postId,
      voteDecision,
    }: ThreadDetailSubmitVoteRequest,
    {
      rejectWithValue,
      getState,
    },
  ) => {
    const apiPath = getIsThreadDetailSSR(getState() as AppState)
      ? getVoteV2Api(threadId, postId)
      : `${THREAD_API}/${threadId}/post/${postId}/vote`;
    const voteResponse = await customFetch(
      apiPath,
      {
        method: 'PUT',
        body: JSON.stringify({ voteDecision }),
      },
      (error, responseInfo) => rejectWithValue(responseInfo),
    ) as ThreadDetailSubmitVoteResponse;
    return voteResponse;
  },
);

export const submitPost = createAsyncThunk(
  'threadDetail/submitPost',
  async (
    {
      threadId,
      id,
      inReplyPostId,
      draft,
      content,
      contentMetadata,
    }: ThreadDetailSubmitPostRequest,
    {
      rejectWithValue,
      getState,
    },
  ) => {
    let apiPath: string;
    if (getIsThreadDetailSSR(getState() as AppState)) {
      apiPath = id ? getPostV2Api(threadId, id) : getSubmitPostV2Api(threadId);
    } else {
      apiPath = id ? getPostApi(threadId, id) : submitPostApi(threadId);
    }
    const submitPostResponse = await customFetch(
      apiPath,
      {
        method: id ? 'PUT' : 'POST',
        body: JSON.stringify({
          content,
          marketplaceId: selectMarketplaceId(getState() as AppState),
          inReplyPostId,
          draft,
          contentMetadata,
        }),
      },
      (error, responseInfo) => rejectWithValue(responseInfo),
    ) as ThreadDetailSubmitPostResponse;
    return submitPostResponse;
  },
);

export const fetchDraft = createAsyncThunk(
  'threadDetail/fetchDraft',
  async (
    {
      threadId,
    }: GetDraftRequest,
    {
      rejectWithValue,
      getState,
    },
  ) => {
    const apiPath = getIsThreadDetailSSR(getState() as AppState)
      ? getDraftsForThreadV2Api(threadId)
      : getDraftsForThreadApi(threadId);
    const draftResponse = await customFetch(
      apiPath,
      undefined,
      (error: Error) => rejectWithValue(error.message),
    ) as GetDraftResponse;
    return draftResponse;
  },
);

/**
 * Reusing the same endpoint for delete post and thread
 * will update once the team comes to a decision
 */
export const deletePost = createAsyncThunk(
  'threadDetail/deletePost',
  async (
    {
      threadId,
      postId,
    }: DeletePostRequest,
    { rejectWithValue, getState },
  ) => {
    const apiPath = getIsThreadDetailSSR(getState() as AppState)
      ? getPostV2Api(threadId, postId)
      : `${THREAD_API}/${threadId}/post/${postId}`;
    const deletePostResponse = customFetch(
      apiPath,
      {
        method: 'DELETE',
        body: JSON.stringify({}),
      },
      (error, responseInfo) => rejectWithValue(responseInfo),
    );
    return deletePostResponse;
  },
);

export const markSolution = createAsyncThunk(
  'threadDetail/markSolution',
  async (
    {
      threadId,
      postId,
      solutionAction,
    }: MarkSolutionRequest,
    { rejectWithValue, getState },
  ) => {
    const apiPath = getIsThreadDetailSSR(getState() as AppState)
      ? getMarkSolutionV2Api(threadId, postId)
      : `${THREAD_API}/${threadId}/post/${postId}/solution`;
    const markSolutionResponse = customFetch(
      apiPath,
      {
        method: 'PUT',
        body: JSON.stringify({ solutionAction }),
      },
      (error, responseInfo) => rejectWithValue(responseInfo),
    );
    return markSolutionResponse;
  },
);

export const fetchPinInfo = createAsyncThunk(
  'app/fetchPinInfo',
  async ({ threadId }: PinInfoRequest, { rejectWithValue, getState }) => {
    const apiPath = getIsThreadDetailSSR(getState() as AppState)
      ? getPinInfoV2Api(threadId)
      : getPinInfoApi(threadId);
    const response = await customFetch(
      apiPath,
      undefined,
      (error, responseInfo) => rejectWithValue(responseInfo),
    ) as PinInfoResponse;
    return response;
  },
);

export const updatePinsForThread = createAsyncThunk(
  'app/updatePinsForThread',
  async (request: UpdatePinsForThreadRequest, { rejectWithValue, getState }) => {
    const apiPath = getIsThreadDetailSSR(getState() as AppState)
      ? getPinThreadV2Api(request.threadId)
      : getPinThreadApi(request.threadId);
    const response = await customFetch(
      apiPath,
      {
        method: 'PUT',
        body: JSON.stringify(request),
      },
      (error: Error) => rejectWithValue(error.message),
    ) as UpdatePinsForThreadResponse;
    return response;
  },
);

/**
 * State management logic
 */

export const threadDetailSlice = createSlice({
  name: 'threadDetail',
  initialState,
  reducers: {
    setFlagPost: (
      state: ThreadDetailState,
      action: PayloadAction<{ isFlagged: boolean, postId: string }>,
    ) => {
      const { postId, isFlagged } = action.payload;
      state.posts[postId].postDetails.isFlagged = isFlagged;
    },
    setShowReplies: (state, action: PayloadAction<{ showReplies: boolean, postId: string }>) => {
      const { postId, showReplies } = action.payload;
      state.posts[postId].showReplies = showReplies;
    },
    setError: (
      state: ThreadDetailState,
      action: PayloadAction<{ postId: string, postError: PostErrors }>,
    ) => {
      const { postId, postError } = action.payload;
      state.posts[postId].errors = addPostResponseStatus<PostErrors>(
        state.posts[postId].errors,
        [postError],
      );
    },
    removeError: (
      state: ThreadDetailState,
      action: PayloadAction<{ errorIndex: number, postId: string }>,
    ) => {
      const { postId, errorIndex } = action.payload;
      const updatedErrors = [...state.posts[postId].errors];
      updatedErrors.splice(errorIndex, 1);
      state.posts[postId].errors = updatedErrors;
    },
    removeSuccess: (state, action: PayloadAction<{ successIndex: number, postId: string }>) => {
      const { postId, successIndex } = action.payload;
      const updatedSuccess = [...state.posts[postId].success];
      updatedSuccess.splice(successIndex, 1);
      state.posts[postId].success = updatedSuccess;
    },
    setSortBy: (
      state: ThreadDetailState,
      action: PayloadAction<SortOptions>,
    ) => {
      state.sortBy = action.payload;
    },
    setFilterBy: (
      state: ThreadDetailState,
      action: PayloadAction<FilterOptions>,
    ) => {
      state.filterBy = action.payload;
    },
    setFocusedPostId: (
      state: ThreadDetailState,
      action: PayloadAction<string>,
    ) => {
      state.focusedPostId = action.payload;
    },
    setDraftPost: (
      state: ThreadDetailState,
      action: PayloadAction<{ parentPostId: string, type: DraftPostType, id?: string } | undefined>,
    ) => {
      state.draftPost = action.payload;
    },
    setDraftContent: (
      state: ThreadDetailState,
      action: PayloadAction<{ content?: string, metadata?: ContentMetadata }>,
    ) => {
      state.draftContent = action.payload.content;
      state.draftContentMetadata = action.payload.metadata;
    },
    resetDraftStatus: (
      state: ThreadDetailState,
    ) => {
      state.draftSubmissionStatus = FetchStatus.idle;
      state.deleteDraftStatus = FetchStatus.idle;
    },
    /**
     * Temporary copy post functionality
     * @todo Replace with fetching the tiny url for the post when
     * backend implementation is complete
     * sim - https://sim.amazon.com/issues/SPCE-3822
     */
    setCopyPostLink: (
      state: ThreadDetailState,
      action: PayloadAction<string>,
    ) => {
      const postId = action.payload;
      const { origin, pathname } = window.location;
      navigator.clipboard.writeText(`${origin}${pathname}?postId=${postId}`);
      state.posts[postId].success = addPostResponseStatus<PostSuccess>(
        state.posts[postId].success,
        [PostSuccess.copyPost],
      );
    },
    setThreadDetailState: (
      state: ThreadDetailState,
      action: PayloadAction<ThreadDetailResponse>,
    ) => {
      Object.assign(state, initialState);
      setThreadState(state, action.payload, {});
      state.isServerInitializedThread = true;
    },
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchThread.rejected, (state: ThreadDetailState, action: {
        meta: {
          arg: ThreadDetailRequest;
          requestId: string;
          requestStatus: 'rejected';
          aborted: boolean;
          condition: boolean;
        }
        error: SerializedError;
        payload: { message: string, responseInfo: {} } | any
      }) => {
        const { direction } = action.meta.arg;
        /**
         *  page level error is displayed when its a failure on initialization
         *  posts is an empty object on page load
         */
        if (action.error.message === CancelFetchThread) {
          state.status = FetchStatus.loading;
        } else {
          state.status = FetchStatus.failed;
        }
        const DEFAULT_ERROR = 'fetch call failed';
        if (Object.keys(state.posts).length === 0) {
          if (action.payload !== undefined
            && action.payload.responseInfo?.data?.message
            && action.payload.responseInfo?.status === 403) {
            // TODO: https://sim.amazon.com/issues/SPCE-9356
            const responseErrors = getErrors(
              action.payload.responseInfo,
              { [ErrorType.hasNoAccess]: PostErrors.hasNoAccessToPrivateDiscussion },
            );
            state.hasNoAccessToPrivateDiscussion = responseErrors.actionErrors
              .includes(PostErrors.hasNoAccessToPrivateDiscussion)
              || false;
          }
        } else if (direction === CursorDirection.previous) {
          state.isPreviousLoading = false;
          state.previousCursor = undefined;
          state.parentPostPreviousError = JSON.stringify(action.payload) || DEFAULT_ERROR;
        } else {
          state.isNextLoading = false;
          state.nextCursor = undefined;
          state.parentPostNextError = JSON.stringify(action.payload) || DEFAULT_ERROR;
        }
      })
      .addCase(fetchThread.pending, (state: ThreadDetailState, action) => {
        const { direction, cursor } = action.meta.arg;
        if (direction === CursorDirection.previous) {
          state.isPreviousLoading = true;
        } else if (direction === CursorDirection.next) {
          state.isNextLoading = true;
        } else {
          state.status = FetchStatus.loading;
        }
        if (!cursor) {
          state.parentPostIds = [];
        }
        state.parentPostNextError = undefined;
        state.parentPostPreviousError = undefined;
      })
      .addCase(fetchThread.fulfilled, (state: ThreadDetailState, action) => {
        setThreadState(state, action.payload, action.meta.arg);
      })
      .addCase(fetchReplies.pending, (state: ThreadDetailState, action) => {
        const { direction, postId: parentPostId } = action.meta.arg;
        const parentPost = state.posts[parentPostId];
        parentPost.errors = [];
        if (direction === CursorDirection.previous) {
          parentPost.isPreviousChildrenPostsLoading = true;
        } else {
          parentPost.isNextChildrenPostsLoading = true;
        }
      })
      .addCase(fetchReplies.fulfilled, (state: ThreadDetailState, action) => {
        const { direction, postId: parentPostId } = action.meta.arg;
        const newReplies = action.payload.replies.entries;
        const parentPost = state.posts[parentPostId];
        const updatedPreviousCursor = action.payload.replies.previousCursor;
        const updatedNextCursor = action.payload.replies.nextCursor;

        // update replies loading state
        if (direction === CursorDirection.previous) {
          parentPost.isPreviousChildrenPostsLoading = false;
        } else {
          parentPost.isNextChildrenPostsLoading = false;
        }

        // update post state with new reply posts
        if (newReplies && newReplies.length > 0) {
          state.posts = addNewPosts(state.posts, newReplies);
          parentPost.childrenPostIds = addPostIds(
            parentPost.childrenPostIds,
            newReplies,
            direction,
          );
        }

        // update cursors based on pagination request direction
        if (direction === CursorDirection.previous) {
          parentPost.previousCursor = newReplies.length > 0
            ? updatedPreviousCursor : undefined;
        } else if (direction === CursorDirection.next) {
          parentPost.nextCursor = newReplies.length > 0
            ? updatedNextCursor : undefined;
        } else {
          parentPost.nextCursor = updatedNextCursor;
          parentPost.previousCursor = updatedPreviousCursor;
        }
      })
      .addCase(fetchReplies.rejected, (state: ThreadDetailState, action) => {
        const { postId, direction } = action.meta.arg;
        state.posts[postId].errors = addPostResponseStatus<PostErrors>(
          state.posts[postId].errors,
          [direction === CursorDirection.next
            ? PostErrors.fetchNextReplies : PostErrors.fetchPreviousReplies]
          ,
        );
      })
      .addCase(submitVote.fulfilled, (state: ThreadDetailState, action) => {
        const { postId, voteDecision } = action.meta.arg;
        const { upVoteCount, downVoteCount } = action.payload;
        state.posts[postId].postDetails.upVoteCount = upVoteCount;
        state.posts[postId].postDetails.downVoteCount = downVoteCount;
        state.posts[postId].postDetails.voteDecision = voteDecision;
      })
      .addCase(submitVote.rejected, (state: ThreadDetailState, action) => {
        const { postId } = action.meta.arg;
        const { actionErrors } = getErrors(
          action.payload as { status: number, data: any },
          commonPostActionErrors,
        );
        if (actionErrors.length === 0) {
          state.posts[postId].errors = addPostResponseStatus<PostErrors>(
            state.posts[postId].errors,
            [PostErrors.voteSubmission],
          );
        } else {
          state.posts[postId].errors = addPostResponseStatus<PostErrors>(
            state.posts[postId].errors,
            actionErrors,
          );
        }
      })
      .addCase(deletePost.fulfilled, (state: ThreadDetailState, action) => {
        const { postId } = action.meta.arg;
        if (state.draftPost?.id === postId) {
          state.draftContent = undefined;
          state.draftContentMetadata = undefined;
          state.draftPost = undefined;
          state.draftReplyId = undefined;
          state.deleteDraftStatus = FetchStatus.complete;
        }
        if (state.posts[postId]) {
          state.posts[postId].success = addPostResponseStatus<PostSuccess>(
            state.posts[postId].success,
            [PostSuccess.deletePost],
          );
          state.posts[postId].postDetails.isDeleted = true;
          state.deletePostStatus = FetchStatus.complete;
        }
      })
      .addCase(deletePost.rejected, (state: ThreadDetailState, action) => {
        const { postId } = action.meta.arg;
        const deletePostActionErrors = {
          ...commonPostActionErrors,
          [ErrorType.postIsMarkedAsSolution]: PostErrors.postIsMarkedAsSolution,
        };
        const { actionErrors } = getErrors(
          action.payload as { status: number, data: any },
          deletePostActionErrors,
        );
        if (state.draftPost?.id === postId) {
          state.draftPostErrors = [PostErrors.deleteDraft];
          state.deleteDraftStatus = FetchStatus.failed;
        }
        if (state.posts[postId]) {
          if (actionErrors.length === 0) {
            state.posts[postId].errors = addPostResponseStatus<PostErrors>(
              state.posts[postId].errors,
              [PostErrors.deletePost],
            );
          } else {
            state.posts[postId].errors = addPostResponseStatus<PostErrors>(
              state.posts[postId].errors,
              actionErrors,
            );
          }
          state.deletePostStatus = FetchStatus.failed;
        }
      })
      .addCase(deletePost.pending, (state: ThreadDetailState, action) => {
        const { postId } = action.meta.arg;
        if (state.draftPost?.id === postId) {
          state.deleteDraftStatus = FetchStatus.loading;
          state.draftPostErrors = [];
        } else {
          state.deletePostStatus = FetchStatus.loading;
        }
      })
      .addCase(markSolution.pending, (state: ThreadDetailState) => {
        state.markSolutionStatus = FetchStatus.loading;
      })
      .addCase(markSolution.fulfilled, (state: ThreadDetailState, action) => {
        const { postId, solutionAction } = action.meta.arg;
        (state.posts[postId].postDetails as ReplyPost).isSolution = solutionAction === 'MARK';
        const opPost = getOpPost(state.posts);
        (state.posts[opPost.id].postDetails as OpPost).solutionId = solutionAction === 'MARK' ? postId : undefined;
        state.solutionPost = solutionAction === 'MARK' ? (state.posts[postId].postDetails as ReplyPost) : undefined;
        state.markSolutionStatus = FetchStatus.complete;
      })
      .addCase(markSolution.rejected, (state: ThreadDetailState, action) => {
        const { postId } = action.meta.arg;
        const { actionErrors } = getErrors(
          action.payload as { status: number, data: any },
          commonPostActionErrors,
        );

        if (actionErrors.length === 0) {
          state.posts[postId].errors = addPostResponseStatus<PostErrors>(
            state.posts[postId].errors,
            [PostErrors.markSolution],
          );
        } else {
          state.posts[postId].errors = addPostResponseStatus<PostErrors>(
            state.posts[postId].errors,
            actionErrors,
          );
        }
        state.markSolutionStatus = FetchStatus.failed;
      })
      .addCase(submitPost.fulfilled, (state: ThreadDetailState, action) => {
        const { draft } = action.meta.arg;
        const { post } = action.payload;
        if (!draft) {
          if (state.draftPost!.type === DraftPostType.edit
            && state.posts[post.id]
          ) {
            state.posts[post.id].postDetails = post;
          } else {
            state.draftReplyId = undefined;
          }
          state.draftPost = undefined;
        } else {
          state.draftPost!.id = action.payload.post.id;
          state.draftReplyId = action.payload.post.id;
        }
        state.draftSubmissionStatus = FetchStatus.complete;
      })
      .addCase(submitPost.rejected, (state: ThreadDetailState, action) => {
        const { draft } = action.meta.arg;
        if (!draft) {
          const postSubmissionErrors = {
            ...commonPostActionErrors,
            [ErrorType.updateWindowCrossed]: PostErrors.updateWindowCrossed,
            [ErrorType.cannotUpdateToDraft]: PostErrors.cannotUpdateToDraft,
          };
          const { fieldErrors, actionErrors } = getErrors(
            action.payload as { status: number, data: any },
            postSubmissionErrors,
          );
          if (fieldErrors.length === 0 && actionErrors.length === 0) {
            state.draftPostErrors = [PostErrors.postSubmission];
          } else {
            state.draftFieldErrors = fieldErrors;
            state.draftPostErrors = actionErrors;
          }
        }
        state.draftSubmissionStatus = FetchStatus.failed;
      })
      .addCase(submitPost.pending, (state: ThreadDetailState) => {
        state.draftSubmissionStatus = FetchStatus.loading;
        state.draftFieldErrors = [];
        state.draftPostErrors = [];
      })
      .addCase(fetchDraft.fulfilled, (state: ThreadDetailState, action) => {
        const { draft, inReplyPost } = action.payload;
        const updatedInReplyPost = inReplyPost && Object.keys(inReplyPost).length > 0
          ? inReplyPost as ReplyPost | OpPost : undefined;
        if (updatedInReplyPost && draft.id) {
          state.draftPost = {
            parentPostId: updatedInReplyPost.id,
            id: draft.id,
            type: DraftPostType.reply,
          };
          state.draftReplyId = draft.id;
          state.draftContent = draft.content;
          state.draftContentMetadata = draft.contentMetadata;
          state.posts = addNewPosts(state.posts, [updatedInReplyPost]);
        }
        state.getDraftStatus = FetchStatus.complete;
      })
      .addCase(fetchDraft.pending, (state: ThreadDetailState) => {
        state.getDraftStatus = FetchStatus.loading;
      })
      .addCase(fetchDraft.rejected, (state: ThreadDetailState) => {
        state.getDraftStatus = FetchStatus.failed;
      })
      .addCase(fetchPinInfo.rejected, (state: ThreadDetailState) => {
        // TODO: Handle specific error information.
        state.pinInfoFetchStatus = FetchStatus.failed;
      })
      .addCase(fetchPinInfo.pending, (state: ThreadDetailState) => {
        state.pinInfoFetchStatus = FetchStatus.loading;
      })
      .addCase(fetchPinInfo.fulfilled, (state: ThreadDetailState, action) => {
        state.pinInfoFetchStatus = FetchStatus.complete;
        state.pinInfoResponse = action.payload;
      })
      .addCase(updatePinsForThread.rejected, (state: ThreadDetailState) => {
        // TODO: Handle specific error information.
        state.pinUpdateStatus = FetchStatus.failed;
      })
      .addCase(updatePinsForThread.pending, (state: ThreadDetailState) => {
        state.pinUpdateStatus = FetchStatus.loading;
      })
      .addCase(updatePinsForThread.fulfilled, (state: ThreadDetailState, action) => {
        const opPost = getOpPost(state.posts);
        state.posts[opPost.id].postDetails = {
          ...state.posts[opPost.id].postDetails,
          ...action.payload as GetPinsForThreadResponse,
        } as OpPost;
        state.pinUpdateStatus = FetchStatus.complete;
      });
  },
});

export const threadDetailActions = threadDetailSlice.actions;

export default threadDetailSlice.reducer;

/**
 * THREAD DETAIL SELECTORS
 */

export const selectThreadDetailPosts = (state: AppState) => state.threadDetail.posts;

export const selectThreadDetailOp = (state: AppState) => getOpPost(state.threadDetail.posts);

export const selectThreadDetailPostIds = (state: AppState) => state.threadDetail.parentPostIds;

export const selectPostById = createSelector(
  [
    selectThreadDetailPosts,
    (state: AppState, postId: string) => postId,
  ],
  (posts, postId) => (posts[postId]),
);

export const selectHasMorePosts = (state: AppState) => !!state.threadDetail.nextCursor;

export const selectHasPreviousPosts = (state: AppState) => !!state.threadDetail.previousCursor;

export const selectIsPreviousLoading = (state: AppState) => state.threadDetail.isPreviousLoading;

export const selectIsNextLoading = (state: AppState) => state.threadDetail.isNextLoading;

export const selectThreadDetailPreviousKey = (state: AppState) => state
  .threadDetail.previousCursor;

export const selectThreadDetailNextKey = (state: AppState) => state.threadDetail.nextCursor;

export const selectThreadDetailFetchStatus = (state: AppState) => state.threadDetail.status;

export const selectThreadDetailParentPostPreviousError = (state: AppState) => state
  .threadDetail
  .parentPostPreviousError;

export const selectThreadDetailParentPostNextError = (state: AppState) => state
  .threadDetail
  .parentPostNextError;

export const selectFilter = (state: AppState) => state.threadDetail.filterBy;

export const selectSort = (state: AppState) => state.threadDetail.sortBy;

export const selectFocusedPostId = (state: AppState) => state.threadDetail.focusedPostId;

export const selectDraftPost = (state: AppState) => state.threadDetail.draftPost;

export const selectDraftReplyId = (state: AppState) => state.threadDetail.draftReplyId;

export const selectDraftStatus = (state: AppState) => state.threadDetail.draftSubmissionStatus;

export const selectDraftContent = (state: AppState) => ({
  content: state.threadDetail.draftContent,
  contentMetadata: state.threadDetail.draftContentMetadata,
});

export const selectGetDraftStatus = (state: AppState) => state.threadDetail.getDraftStatus;

export const selectDeleteDraftStatus = (state: AppState) => state.threadDetail.deleteDraftStatus;

export const selectReplyCount = (state: AppState) => state.threadDetail.replyCount;

export const selectSolutionPost = (state: AppState) => state.threadDetail.solutionPost;

export const selectDraftFieldErrors = (state: AppState) => state.threadDetail.draftFieldErrors;

export const selectDraftPostErrors = (state: AppState) => state.threadDetail.draftPostErrors;

export const selectMarkSolutionStatus = (state: AppState) => state.threadDetail.markSolutionStatus;

export const selectDeletePostStatus = (state: AppState) => state.threadDetail.deletePostStatus;

export const selectIsServerInitializedThread = (state: AppState) => state
  .threadDetail.isServerInitializedThread;
